import type {
  IBiomapArtifactDetail,
  IBiomapScenarioArtifact,
  IBiomapScenarioCatalog,
  IBiomapScenarioSelection,
} from '../../interfaces/strategic-view/IBiomapScenarioAnalysis';

function sortArtifacts(items: IBiomapScenarioArtifact[]): IBiomapScenarioArtifact[] {
  return [...items].sort((left, right) =>
    left.year - right.year ||
    (left.scenario ?? '').localeCompare(right.scenario ?? '') ||
    left.artifactUuid.localeCompare(right.artifactUuid),
  );
}

export function buildBiomapScenarioCatalog(
  artifacts: readonly IBiomapArtifactDetail[],
  executionUuid: string,
  rasterLayerUuid: string,
): IBiomapScenarioCatalog {
  const overall = artifacts.flatMap((artifact): IBiomapScenarioArtifact[] => {
    if (
      artifact.product !== 'msa' ||
      artifact.artifact_type !== 'raster' ||
      artifact.component != null ||
      artifact.raster_layer_uuid !== rasterLayerUuid ||
      !artifact.raster_source_uuid ||
      artifact.year == null
    ) {
      return [];
    }

    return [{
      artifactUuid: artifact.uuid,
      artifactKey: artifact.artifact_key,
      executionUuid: artifact.execution_uuid || executionUuid,
      layerKey: artifact.layer_key ?? '',
      rasterLayerUuid,
      rasterSourceUuid: artifact.raster_source_uuid,
      year: artifact.year,
      scenario: artifact.scenario?.trim() || null,
      metadata: artifact.metadata ?? {},
    }];
  });

  return {
    executionUuid,
    rasterLayerUuid,
    baselines: sortArtifacts(overall.filter((artifact) => !artifact.scenario)),
    scenarioArtifacts: sortArtifacts(overall.filter((artifact) => !!artifact.scenario)),
  };
}

export function getBiomapScenarioBaseline(
  catalog: IBiomapScenarioCatalog | null | undefined,
  sourceUuid: string | null | undefined,
): IBiomapScenarioArtifact | null {
  return catalog?.baselines.find((artifact) => artifact.rasterSourceUuid === sourceUuid) ?? null;
}

export function getBiomapScenarioTarget(
  catalog: IBiomapScenarioCatalog | null | undefined,
  scenario: string | null | undefined,
  year: number | null | undefined,
): IBiomapScenarioArtifact | null {
  if (!catalog || !scenario || year == null) {
    return null;
  }

  return catalog.scenarioArtifacts.find(
    (artifact) => artifact.scenario === scenario && artifact.year === year,
  ) ?? null;
}

export function getBiomapScenarioCodes(
  catalog: IBiomapScenarioCatalog | null | undefined,
  baselineYear: number | null | undefined,
): string[] {
  if (!catalog || baselineYear == null) {
    return [];
  }

  return [...new Set(
    catalog.scenarioArtifacts
      .filter((artifact) => artifact.year > baselineYear)
      .map((artifact) => artifact.scenario)
      .filter((scenario): scenario is string => !!scenario),
  )].sort((left, right) => left.localeCompare(right));
}

export function getBiomapScenarioYears(
  catalog: IBiomapScenarioCatalog | null | undefined,
  scenario: string | null | undefined,
  baselineYear: number | null | undefined,
): number[] {
  if (!catalog || !scenario || baselineYear == null) {
    return [];
  }

  return [...new Set(
    catalog.scenarioArtifacts
      .filter((artifact) => artifact.scenario === scenario && artifact.year > baselineYear)
      .map((artifact) => artifact.year),
  )].sort((left, right) => left - right);
}

export function getBiomapComparisonScenarioCodes(
  catalog: IBiomapScenarioCatalog | null | undefined,
  primaryScenario: string | null | undefined,
  targetYear: number | null | undefined,
): string[] {
  if (!catalog || targetYear == null) {
    return [];
  }

  return [...new Set(
    catalog.scenarioArtifacts
      .filter((artifact) => artifact.year === targetYear && artifact.scenario !== primaryScenario)
      .map((artifact) => artifact.scenario)
      .filter((scenario): scenario is string => !!scenario),
  )].sort((left, right) => left.localeCompare(right));
}

export function normalizeBiomapScenarioSelection(
  catalog: IBiomapScenarioCatalog,
  selection: Partial<IBiomapScenarioSelection> = {},
): IBiomapScenarioSelection {
  const baseline =
    getBiomapScenarioBaseline(catalog, selection.baselineSourceUuid) ??
    [...catalog.baselines].sort((left, right) => right.year - left.year)[0] ??
    null;

  if (!baseline) {
    return {
      baselineSourceUuid: null,
      scenario: null,
      targetYear: null,
      comparisonScenarios: [],
    };
  }

  const scenarios = getBiomapScenarioCodes(catalog, baseline.year);
  const scenario = selection.scenario && scenarios.includes(selection.scenario)
    ? selection.scenario
    : scenarios[0] ?? null;
  const years = getBiomapScenarioYears(catalog, scenario, baseline.year);
  const targetYear = selection.targetYear != null && years.includes(selection.targetYear)
    ? selection.targetYear
    : years[years.length - 1] ?? null;
  const availableComparisons = getBiomapComparisonScenarioCodes(catalog, scenario, targetYear);

  return {
    baselineSourceUuid: baseline.rasterSourceUuid,
    scenario,
    targetYear,
    comparisonScenarios: (selection.comparisonScenarios ?? []).filter((item) =>
      availableComparisons.includes(item),
    ),
  };
}
